export type BlindQaResult = 'acceptable' | 'rejected'
export type BlindRejectReason = 'vowel_leak' | 'consonant_leak' | 'schwa' | 'wrong_quality' | 'too_short' | 'too_long' | 'unnatural' | 'other'

export interface BlindCandidateReview {
  result?: BlindQaResult
  rejectReason?: BlindRejectReason
}

export interface BlindPhonemeReview {
  order: string[]
  playedCandidateIds: string[]
  revealed: boolean
  candidates: Record<string, BlindCandidateReview>
}

export interface BlindQaState {
  version: 1
  phonemes: Record<string, BlindPhonemeReview>
}

export interface BlindCandidateIdentity {
  candidateId: string
  phonemeId: string
}

export const blindPhonemeOrder = ['phoneme_t', 'phoneme_p', 'phoneme_s', 'phoneme_short_a_ae', 'phoneme_short_i_ih', 'phoneme_n'] as const
export const blindRejectReasons: BlindRejectReason[] = ['vowel_leak', 'consonant_leak', 'schwa', 'wrong_quality', 'too_short', 'too_long', 'unnatural', 'other']

export const emptyBlindQaState = (): BlindQaState => ({ version: 1, phonemes: {} })

export const canJudgeCandidate = (review: BlindPhonemeReview | undefined, candidateId: string) => Boolean(review && !review.revealed && review.order.includes(candidateId) && review.playedCandidateIds.includes(candidateId))
export const canRevealSources = (review: BlindPhonemeReview | undefined) => Boolean(review && review.order.length === 3 && new Set(review.order).size === 3 && review.order.every((id) => review.playedCandidateIds.includes(id) && review.candidates[id]?.result))

export const shuffleCandidateIds = (ids: string[], random: () => number = Math.random) => {
  const shuffled = [...ids]
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapWith = Math.floor(random() * (index + 1))
    ;[shuffled[index], shuffled[swapWith]] = [shuffled[swapWith]!, shuffled[index]!]
  }
  return shuffled
}

const sameCandidateSet = (left: string[], right: string[]) => left.length === right.length && [...left].sort().join('|') === [...right].sort().join('|')

export const normalizeBlindQaState = (
  current: BlindQaState,
  records: BlindCandidateIdentity[],
  random: () => number = Math.random,
): BlindQaState => {
  const next: BlindQaState = { version: 1, phonemes: { ...current.phonemes } }
  blindPhonemeOrder.forEach((phonemeId) => {
    const candidateIds = records.filter((record) => record.phonemeId === phonemeId).map((record) => record.candidateId)
    if (!candidateIds.length) return
    const prior = current.phonemes[phonemeId]
    const order = prior && sameCandidateSet(prior.order, candidateIds) ? prior.order : shuffleCandidateIds(candidateIds, random)
    const allowed = new Set(candidateIds)
    next.phonemes[phonemeId] = {
      order,
      playedCandidateIds: (prior?.playedCandidateIds ?? []).filter((id) => allowed.has(id)),
      revealed: prior?.revealed ?? false,
      candidates: Object.fromEntries(Object.entries(prior?.candidates ?? {}).filter(([id]) => allowed.has(id))),
    }
  })
  return next
}
